import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ReactComponent as TrashIcon } from 'assets/icons/tresh.svg';
import clsx from 'clsx';
import IconButton from 'components/IconButton/IconButton';
import Table from 'components/Table/Table';
import TableBody from 'components/Table/TableBody';
import TableCell from 'components/Table/TableCell';
import TableHead from 'components/Table/TableHead';
import TableRow from 'components/Table/TableRow';
import formattedData from 'utils/formattedData';
import formattedNumberWithComma from 'utils/formattedNumberWithComma';
import getNaceLabel from 'utils/getNaceLabel';

import type IStakeholderSupplierItem from 'store/slices/materialAssessment/interfaces/IStakeholderSupplierItem';
import type ISuppliersTable from './interfaces/ISuppliersTable';

import styles from './scss/SuppliersTable.module.scss';

function SuppliersTable({
  items, headerCells, isActionsVisible, isInfoTable, onDeleteItem,
  loadMoreSuppliers, hasMore, naceCodeTreeData,
}: ISuppliersTable) {
  const { t } = useTranslation();
  const hyphen = t('common.hyphen');

  const cells = headerCells.filter(({ id }) => {
    if (id === 'actions' || id === 'action') {
      return isActionsVisible;
    }
    return true;
  });

  const cellsData = (item: IStakeholderSupplierItem) => [
    { id: item.uniqueIdentifier, value: item.uniqueIdentifier },
    { id: item.code, value: item.code },
    { id: item.sourceVCMId, value: item.sourceVCMId || hyphen },
    { id: item.valueChainSection, value: item.valueChainSection },
    { id: item.upstreamTier, value: item.upstreamTier },
    { id: item.ownership, value: item.ownership },
    { id: item.businessUnitType, value: item.businessUnitType },
    {
      id: item.equityShareholding,
      value: item.equityShareholding,
      format: (val: number) => `${formattedNumberWithComma(val)} %`,
    },
    {
      id: item.financialControl,
      value: item.financialControl,
      format: (val: number) => `${formattedNumberWithComma(val)} %`,
    },
    { id: item.dateOpened, value: formattedData(item.dateOpened) },
    { id: item.dateClosed, value: formattedData(item.dateClosed) },
    {
      id: item.naceSection,
      value: getNaceLabel({ naceSection: item.naceSection, naceCodeTreeData }).naceSectionLabel || hyphen,
    },
    {
      id: item.naceCode,
      value: getNaceLabel({
        naceCode: item.naceCode,
        naceSection: item.naceSection,
        naceCodeTreeData,
      }).naceCodeLabel || hyphen,
    },
    { id: item.euTaxonomyCode, value: item.euTaxonomyCode },
  ];

  return (
    <div className={styles.root}>
      <InfiniteScroll
        dataLength={items?.length || 0}
        next={loadMoreSuppliers}
        hasMore={hasMore}
        loader={false}
      >
        <div
          className={styles.tableBodyScroll}
        >
          <Table
            className={clsx(styles.table, {
              [styles.tableInfoTable]: isInfoTable,
            })}
          >
            <TableHead className={clsx(styles.header, {
              [styles.headerWithoutActions]: !isActionsVisible,
              [styles.headerInfoTable]: isInfoTable,
            })}
            >
              {cells.map(({
                id, label,
              }) => (
                <TableCell head className={styles.tableCell} key={id}>
                  <div>
                    <span>{t(label)}</span>
                  </div>
                </TableCell>
              ))}
            </TableHead>
            <TableBody className={clsx(styles.body, { [styles.bodyWithoutActions]: !isActionsVisible })}>
              {items ? items?.map((item, index) => (
                <TableRow key={[item.id, index].join('_')}>
                  <TableCell>
                    <div className={styles.staticCell}>
                      <span>{item.name}</span>
                    </div>
                  </TableCell>
                  {cellsData(item).map((cell, indexCell) => (
                    <TableCell key={[cell.id, indexCell].join('_')}>
                      {(() => {
                        if (!cell.value) return hyphen;
                        if (cell.format) return cell.format(cell.value);
                        return cell.value;
                      })()}
                    </TableCell>
                  ))}
                  <TableCell>
                    <div className={styles.profitCenterLinksContainer}>
                      <div className={styles.wrapper}>
                        {item.profitCenterLinks.slice(0, 3).map((profitCenterLink) => (
                          <div key={profitCenterLink.profitCenterId}>
                            {profitCenterLink.profitCenterId}
                          </div>
                        ))}
                        {item.profitCenterLinks.length > 3 && (
                        <div>...</div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {item.addressCountry || hyphen}
                  </TableCell>
                  <TableCell>
                    {item.addressState || hyphen}
                  </TableCell>
                  <TableCell>
                    {item.addressCity || hyphen}
                  </TableCell>
                  <TableCell>
                    {item.addressPostCode || hyphen}
                  </TableCell>
                  <TableCell>
                    {item.addressStreet || hyphen}
                  </TableCell>
                  <TableCell>
                    {item.addressBuilding || hyphen}
                  </TableCell>
                  <TableCell>
                    {item.addressCoordinates || hyphen}
                  </TableCell>

                  {isActionsVisible ? (
                    <TableCell>
                      <IconButton
                        icon={<TrashIcon />}
                        onClick={() => onDeleteItem?.(item.id)}
                      />
                    </TableCell>
                  ) : null}
                </TableRow>
              )) : null}
            </TableBody>
          </Table>
        </div>
      </InfiniteScroll>
    </div>
  );
}

export default SuppliersTable;
